import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Switch,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  CssBaseline,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  Tooltip,
  ToggleButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Fade,
  Chip,
  Stack
} from '@mui/material';
import { styled, ThemeProvider, alpha } from '@mui/material/styles';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import ClassIcon from '@mui/icons-material/Class'; // Placeholder for Empty State

import axiosInstance from "../../api/axiosConfig";
import adminTheme from '../../theme';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import Lottie from 'lottie-react';
import animationData from '../../assets/icons/no-results.json';

// --- Styled Components (Matching ClassManagement Exactly) ---

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  boxShadow: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: theme.palette.primary.light,
  },
}));

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

// --- Main Component ---

const SectionManagement = () => {
  const schoolId = localStorage.getItem('schoolId') || "1";
  
  // Data States
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  
  // Dialog States
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [sectionName, setSectionName] = useState("");
  
  // UI/UX States
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Filters & Views
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewMode, setViewMode] = useState("list");

  // --- API Calls ---
  const fetchSections = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/sections?schoolId=${schoolId}`);
      setSections(response.data);
    } catch (error) {
      showSnackbar("Failed to fetch sections", 'error');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    setLoading(true);
    fetchSections();
  }, [fetchSections]);

  // --- Handlers ---
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const handleStatusToggle = async (sectionId) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`/api/sections/status/${sectionId}`);
      await fetchSections();
      showSnackbar("Section status updated");
    } catch (error) {
      showSnackbar("Failed to update section status", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSectionDetails = async () => {
    if (!sectionName.trim()) return showSnackbar("Section Name is required", 'warning');
    setActionLoading(true);
    try {
      await axiosInstance.put(`/api/sections/${selectedSection.sectionId}`, { ...selectedSection, sectionName });
      await fetchSections();
      setOpenEditDialog(false);
      showSnackbar("Section details updated successfully");
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to update section details", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddSection = async () => {
    if (!sectionName.trim()) return showSnackbar("Section Name is required", 'warning');
    setActionLoading(true);
    try {
      await axiosInstance.post("/api/sections", { sectionName, schoolId, status: "Active" });
      await fetchSections();
      setOpenAddDialog(false);
      setSectionName("");
      showSnackbar("Section added successfully");
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to add section.", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm("Are you sure you want to delete this section? This action cannot be undone.")) {
      setActionLoading(true);
      try {
        await axiosInstance.delete(`/api/sections/${sectionId}`);
        await fetchSections();
        showSnackbar("Section deleted successfully");
      } catch (error) {
        showSnackbar("Failed to delete section.", 'error');
      } finally {
        setActionLoading(false);
      }
    }
  };

  // --- Filters ---
  const filteredSections = useMemo(() => {
    return sections.filter((s) => {
      const matchesSearch = s.sectionName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "All" || s.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [sections, searchQuery, statusFilter]);

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline /> {/* KILLS THE BLACK BACKGROUND BUG GLOBALLY */}

      <PageContainer sx={{ width: '100%' }}>
        <AdminPageHero
          title="Section Management"
          subtitle="Create school-wide section masters here, then assign them to classes separately."
          metrics={[
            { label: "Sections", value: sections.length },
            { label: "Active", value: sections.filter((item) => item.status === "Active").length },
            { label: "Inactive", value: sections.filter((item) => item.status !== "Active").length },
          ]}
          actions={(
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setSectionName(""); setOpenAddDialog(true); }}
              startIcon={<AddIcon />}
              disableElevation
              sx={{ py: 1, px: 2 }}
            >
              Add New Section
            </Button>
          )}
          sx={{ mb: 3 }}
        />

        <AdminPanelCard
          title="Sections Directory"
          subtitle="Search reusable section masters, filter by status, and keep them ready for class assignment."
          sx={{ mb: 3 }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap" flexGrow={1} maxWidth={600}>
              <TextField
                placeholder="Search sections..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flexGrow: 1,
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ bgcolor: "background.paper" }}
                >
                  <MenuItem value="All">All Statuses</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                "& .MuiToggleButton-root": {
                  border: 0,
                  px: 1.2,
                },
                "& .Mui-selected": {
                  bgcolor: alpha(adminTheme.palette.primary.main, 0.12),
                  color: "primary.main",
                }
              }}
            >
              <Tooltip title="Grid View">
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
              </Tooltip>
              <Tooltip title="List View">
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </AdminPanelCard>

        {/* --- Main Content --- */}
        {loading ? (
          <AdminPanelCard title="Sections" subtitle="Loading section records.">
            <Stack spacing={2}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    height: 64,
                    borderRadius: 1.5,
                    bgcolor: alpha("#94A3B8", 0.14),
                  }}
                />
              ))}
            </Stack>
          </AdminPanelCard>
        ) : filteredSections.length > 0 ? (
          
          viewMode === "grid" ? (
            /* --- GRID VIEW --- */
            <Grid container spacing={3}>
              {filteredSections.map((section) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={section.sectionId}>
                  <StyledCard>
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 3 }}>
                      
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexGrow={1}>
                        <Typography variant="h6" color="text.primary" fontWeight="bold" noWrap title={section.sectionName}>
                          {section.sectionName || "Unnamed Section"}
                        </Typography>
                        <Tooltip title={`Mark as ${section.status === 'Active' ? 'Inactive' : 'Active'}`}>
                          <Switch
                            checked={section.status === "Active"}
                            onChange={() => handleStatusToggle(section.sectionId)}
                            color="success"
                            size="small"
                            disabled={actionLoading}
                            sx={{ mr: -1, mt: -0.5 }}
                          />
                        </Tooltip>
                      </Box>

                      {/* Actions Footer */}
                      <Box display="flex" justifyContent="flex-end" gap={0.5} pt={2} borderTop={1} borderColor="divider">
                        <Tooltip title="Edit Section">
                          <IconButton size="small" onClick={() => { setSelectedSection(section); setSectionName(section.sectionName || ""); setOpenEditDialog(true); }}>
                            <EditIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Section">
                          <IconButton size="small" onClick={() => handleDeleteSection(section.sectionId)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            /* --- LIST VIEW --- */
            <AdminPanelCard
              title="Sections List"
              subtitle="Manage section names, status, and actions from one table."
            >
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" variant="outlined" label={`Sections: ${filteredSections.length}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Active: ${filteredSections.filter((s) => s.status === "Active").length}`}
                    />
                  </Stack>
                </Box>
                <Table sx={{ minWidth: 620 }}>
                  <TableHead sx={{ bgcolor: "#F5F7FB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: 56 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Section Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSections.map((section, index) => (
                      <TableRow
                        key={section.sectionId}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(even)': { bgcolor: alpha("#0F172A", 0.02) },
                        }}
                      >
                        <TableCell sx={{ color: "text.secondary", fontWeight: 700 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{section.sectionName || "Unnamed Section"}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={section.status || "Unknown"}
                              sx={{
                                minWidth: 70,
                                borderColor: section.status === "Active" ? alpha(adminTheme.palette.success.main, 0.4) : alpha(adminTheme.palette.divider, 0.8),
                                color: section.status === "Active" ? adminTheme.palette.success.main : adminTheme.palette.text.secondary,
                                backgroundColor: section.status === "Active" ? alpha(adminTheme.palette.success.main, 0.1) : "transparent",
                              }}
                            />
                            <Switch
                              checked={section.status === "Active"}
                              onChange={() => handleStatusToggle(section.sectionId)}
                              color="success"
                              size="small"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setSelectedSection(section); setSectionName(section.sectionName || ""); setOpenEditDialog(true); }}>
                              <EditIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteSection(section.sectionId)}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AdminPanelCard>
          )

        ) : (
          /* --- EMPTY STATE --- */
          <AdminPanelCard>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              <Box width={150} height={150} mb={2} opacity={0.8}>
                {animationData ? (
                   <Lottie animationData={animationData} loop autoplay style={{ height: "100%", width: "100%" }} />
                ) : (
                   <ClassIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                )}
              </Box>
              <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                {searchQuery || statusFilter !== "All" ? "No matches found" : "No sections added yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3} textAlign="center" maxWidth={400}>
                {searchQuery || statusFilter !== "All"
                  ? "Try adjusting your search or filter settings."
                  : "Sections divide your classes into smaller groups for better organization. Add your first section to get started."}
              </Typography>
              {!(searchQuery || statusFilter !== "All") && (
                <Button variant="contained" onClick={() => { setSectionName(""); setOpenAddDialog(true); }} disableElevation>
                  Add First Section
                </Button>
              )}
            </Box>
          </AdminPanelCard>
        )}

        {/* --- DIALOGS --- */}

        {/* Add Section Dialog */}
        <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Add New Section</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              <TextField 
                autoFocus 
                label="Section Name" 
                placeholder="e.g. Section A, Biology Lab" 
                value={sectionName} 
                onChange={(e) => setSectionName(e.target.value)} 
                required 
                fullWidth 
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setOpenAddDialog(false)} color="inherit" disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleAddSection} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} color="inherit" /> : "Create Section"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Section Dialog */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Edit Section Details</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              <TextField 
                autoFocus 
                label="Section Name" 
                value={sectionName} 
                onChange={(e) => setSectionName(e.target.value)} 
                required 
                fullWidth 
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setOpenEditDialog(false)} color="inherit" disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleSaveSectionDetails} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "#fff", fontWeight: 500, borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </PageContainer>
    </ThemeProvider>
  );
};

export default SectionManagement;
